import * as Sentry from '@sentry/react';
import {
  List as F7List,
  ListItem as F7ListItem,
  Fab,
  ListGroup,
  ListInput,
  f7,
} from 'framework7-react';
import { Input as F7Input } from 'framework7-react';
import { FC, Fragment, useMemo, useState } from 'react';

import { PublicFeedEvent } from '@shared/types/event';
import { event } from 'cypress/types/jquery';
import * as dayjs from 'dayjs';
import { motion } from 'framer-motion';
import { groupBy } from 'lodash';

import {
  FeedEvent,
  useCurrentUserEventRsvpsQuery,
  useEventQuery,
  useUnauthorizedEventsQuery,
} from '@queries/EventQueries';
import { useCurrentUserSeriesPlannerPlansQuery, useSeriesByTagSlug } from '@queries/TagQueries';
import { useCurrentUser, useInterestQuery } from '@queries/UserQueries';

import { useDynamicActionMutation } from '@mutations/AppMutations';
import { useUpdateSeriesPlannerAdditionalPlansMutation } from '@mutations/TagMutations';

import { useEventMetaFilter } from '@hooks/EventHooks/useEventMetaFilter';
import { useAnalytics } from '@hooks/useAnalytics';
import { useEventFilters } from '@hooks/useEventFilters';
import { useReferredBy } from '@hooks/useReferredBy';

import { useLocation, useNavigate, useParams, useRouter } from '@router/index';

import { createScreenComponent } from '@utilities/createScreenComponent';
import { getDatesInRange } from '@utilities/getDatesInRange';
import { mapStringToParagraphs } from '@utilities/mapStringToParagraphs';

import Button from '@components/buttons/Button';
import IconButton from '@components/buttons/IconButton';
import EventCard, {
  EventActions,
  EventHeader,
  EventImage,
  PromoterHeader,
} from '@components/cards/EventCard';
import { VenueCard } from '@components/cards/VenueCard';
import { ChipRadioOptions } from '@components/chips/ChipRadioOptions';
import { EventChip } from '@components/chips/EventChip';
import { DatePicker } from '@components/form/DatePicker';
import { DateRangePicker } from '@components/form/DateRangePicker';
import { FormLabel } from '@components/form/FormLabel';
import { Input } from '@components/form/Input';
import { SearchField } from '@components/form/SearchField/SearchField';
import { Textarea } from '@components/form/Textarea';
import { FontAwesomeIcon } from '@components/icons/FontAwesomeIcon';
import { CopyText } from '@components/layout/CopyText/CopyText';
import { EmptyState } from '@components/layout/EmptyState/EmptyState';
import { HorizontalScroll } from '@components/layout/HorizontalScroll/HorizontalScroll';
import { LinkBanner } from '@components/layout/LinkBanner/LinkBanner';
import { List } from '@components/layout/List/List';
import { Screen } from '@components/layout/Screen/Screen';
import { Skeleton } from '@components/layout/Skeleton/Skeleton';
import { TitleToolbar } from '@components/layout/TitleToolbar/TitleToolbar';

import { useAuthGate } from '@screens/LogInScreen/AuthGateContext';
import prideSvgUrl from '@screens/entertainer/EntertainerDetailsScreen/assets/pride.svg';
import { useEventRsvpActions } from '@screens/event/EventRsvpScreen/hooks/useEventRsvpActions';

import { EventRsvpPopup } from './components/EventRsvpPopup';

const RsvpCardListItemActions: FC<{ event: PublicFeedEvent; onInviteClick?: () => void }> = ({
  event,
  onInviteClick,
}) => {
  const { rsvp, handleMarkAttending, handleMarkInterested } = useEventRsvpActions({
    eventId: event._id,
    eventTitle: event.title,
  });

  const isCurrentUserAttending = rsvp?.rsvp === 'yes';
  const isCurrentUserInterested = rsvp?.rsvp !== 'yes' && rsvp?.eventInterest === 'interested';

  return (
    <div className="mt-2 border-0 pt-0" style={{ fontSize: 12 }}>
      <div className="d-flex gap-4">
        <div
          role="button"
          onClick={(e) => {
            e.stopPropagation();
            handleMarkAttending();
          }}
          className="d-flex align-items-center gap-2"
        >
          <div style={{ fontSize: 14 }}>
            <FontAwesomeIcon
              faStyle={isCurrentUserAttending ? 'fas' : 'far'}
              name={isCurrentUserAttending ? 'calendar-check' : 'calendar-plus'}
              style={{ color: isCurrentUserAttending ? 'var(--color-green)' : undefined }}
            />
          </div>
          <div>
            <span style={{ display: 'inline-block' }}>Attending</span>
          </div>
        </div>
        <div
          role="button"
          onClick={(e) => {
            e.stopPropagation();
            handleMarkInterested();
          }}
          className="d-flex align-items-center gap-2"
        >
          <div style={{ fontSize: 14 }}>
            <FontAwesomeIcon
              faStyle={isCurrentUserInterested ? 'fas' : 'far'}
              name="star"
              style={{ color: isCurrentUserInterested ? 'var(--color-yellow)' : undefined }}
            />
          </div>
          <div>
            <span style={{ display: 'inline-block' }}>Interested</span>
          </div>
        </div>
        {onInviteClick && (
          <div
            role="button"
            onClick={(e) => {
              e.stopPropagation();
              onInviteClick();
            }}
            className="d-flex align-items-center gap-2"
          >
            <div style={{ fontSize: 14 }}>
              <FontAwesomeIcon faStyle="far" name="paper-plane" />
            </div>
            <div>
              <span style={{ display: 'inline-block' }}>Invite</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const FILTER_VALUES = ['isDanceParty', 'isSapphic', 'isDragShow', 'isPrideWeekend'];
const IS_MISSED_EVENTS_ENABLED = false;
interface SeriesFilterPlannerScreenProps {
  tagSlug: string;
  filter: string;
}

export const SeriesFilterPlannerScreen = createScreenComponent<SeriesFilterPlannerScreenProps>(
  ({ tagSlug, filter, back }) => {
    const { track } = useAnalytics();
    const { series } = useSeriesByTagSlug(tagSlug);
    const { navigate, back: navigateBack } = useRouter();
    const { updateSeriesPlannerAdditionalPlansAsync } =
      useUpdateSeriesPlannerAdditionalPlansMutation();
    const { currentUser } = useCurrentUser();
    const [selectedEvents, setSelectedEvents] = useState<
      Record<string, 'attending' | 'interested' | undefined>
    >({});
    const [additionalPlans, setAdditionalPlans] = useState<string>();

    const { createAuthGateHandler } = useAuthGate();

    const [currentEvent, setCurrentEvent] = useState<PublicFeedEvent | undefined>(undefined);

    const continueButtonText = useMemo(() => {
      return `View My Plans`;
    }, []);

    const handleContinueClick = () => {
      createAuthGateHandler(async () => {
        if (currentUser?._id) {
          navigate(`/noauth/ucc/${currentUser?._id}`, {
            state: { back: `/noauth/series/${tagSlug}/filter-planner/${filter}` },
          });
        } else {
          requestAnimationFrame(() => {
            navigate(`/noauth/ucc/${currentUser?._id}`, {
              state: { back: `/noauth/series/${tagSlug}/filter-planner/${filter}` },
            });
          });
        }
      });
    };

    const {
      eventsAreReady,
      events = [],
      eventsMeta,
    } = useUnauthorizedEventsQuery(
      {
        startDate: dayjs().format('YYYY-MM-DD'),
        tagIds: [series?._id],
        forceSupply: 'true',
        rsvpData: 'ignore',
      },
      Boolean(series?.city?._id && series._id),
    );
    const { eventRsvps } = useCurrentUserEventRsvpsQuery();

    const { updateFilterValue, currentFilter, filteredEvents } = useEventMetaFilter(
      events,
      eventsMeta?.filters ?? [],
      filter,
    );

    const filteredEventsGroupedByDate = useMemo(() => {
      return groupBy(filteredEvents, 'startDate');
    }, [filteredEvents]);

    const { attendingCount, interestedCount } = useMemo(() => {
      if (!eventRsvps) {
        return {
          attendingCount: 0,
          interestedCount: 0,
        };
      }

      return {
        attendingCount:
          filteredEvents?.filter((event) => eventRsvps[event._id]?.rsvp === 'yes').length ?? 0,
        interestedCount:
          filteredEvents?.filter(
            (event) =>
              eventRsvps[event._id]?.rsvp !== 'yes' &&
              eventRsvps[event._id]?.eventInterest === 'interested',
          ).length ?? 0,
      };
    }, [filteredEvents, eventRsvps]);

    const handleEventClick = (event: PublicFeedEvent) => {
      track('Click Event', { eventId: event._id });
      setCurrentEvent(event);
    };

    const { dynamicActionAsync } = useDynamicActionMutation();
    const handleSubmitAdditionalPlans = () => {
      createAuthGateHandler(async () => {
        // await dynamicActionAsync({
        //   action:
        // });
      });
    };

    return (
      <Screen
        name="Series Filter Planner Screen"
        hideToolbar
        headerProps={{
          back: { to: back ?? `/noauth/series/${tagSlug}?tab=rsvpToEvents` },
          title: <Screen.GagglTitle />,
        }}
        bottomToolbar={
          <Screen.BottomToolbar height={78} maxWidth={425}>
            <div className="w-100 pb-1">
              <div className="mb-1" style={{ fontSize: '12px', opacity: 0.7, textAlign: 'center' }}>
                <span>
                  {[`${attendingCount} attending`, `${interestedCount} interested`].join(' • ')}
                </span>
              </div>
              <Button
                roundness="rounded"
                endIcon={<FontAwesomeIcon name="chevron-right" />}
                disabled={attendingCount === 0 && interestedCount === 0}
                onClick={handleContinueClick}
              >
                {continueButtonText}
              </Button>
            </div>
          </Screen.BottomToolbar>
        }
      >
        <Screen.Content maxWidth={425}>
          {series && currentFilter ? (
            <>
              <TitleToolbar
                text={<div style={{ fontSize: '1.75rem' }}>{series?.title}</div>}
                className="mb-0"
              />

              <div className="mb-2">
                <CopyText
                  startIcon={<FontAwesomeIcon name="link" />}
                  text="https://sapphicdenver.com"
                  clickAction="share"
                  buttonText={{ copy: 'Share' }}
                />
              </div>

              <p className="mt-0">
                Denver's sapphic scene is taking off! Use this planner to help you find what's going
                on and keep track of everything you want to do.
              </p>

              <TitleToolbar
                text={
                  <div className="d-flex align-items-center gap-2">
                    <img
                      src={currentFilter?.cta?.suggestion?.iconUrl}
                      alt={currentFilter?.label}
                      style={{ width: 24, height: 24, objectFit: 'contain', display: 'block' }}
                    />
                    <div>{`${currentFilter?.label} Events`}</div>
                  </div>
                }
                size="sm"
                infoButton={{
                  title: 'How to use the planner',
                  text: 'Press "Attending" or "Interested" when you find an event you want to add to your planner, and use the filters to find events that match your interests.',
                }}
                action={
                  <Button
                    className="py-1 px-3"
                    size="sm"
                    startIcon={<FontAwesomeIcon name="plus" />}
                    onClick={() =>
                      navigate(`/noauth/series/${tagSlug}/add-rsvp-event?filter=${filter}`, {
                        state: {
                          back: `/noauth/series/${tagSlug}/filter-planner/${filter}`,
                        },
                      })
                    }
                  >
                    Add Event
                  </Button>
                }
              />

              {eventsAreReady ? (
                <>
                  {filteredEvents.length ? (
                    <F7List
                      className="mt-0"
                      dividersIos
                      outlineIos
                      mediaList
                      style={{ marginLeft: '-1rem', marginRight: '-1rem' }}
                    >
                      {Object.entries(filteredEventsGroupedByDate).map(
                        ([currentDate, groupEvents]) => (
                          <ListGroup>
                            <F7ListItem
                              title={dayjs(currentDate).format('dddd, MMMM D, YYYY')}
                              groupTitle
                            />
                            {groupEvents.map((event) => (
                              <F7ListItem
                                key={event._id}
                                title={event.title}
                                subtitle={event.timeDetails.split(' at ')[1]}
                              >
                                <div slot="media" onClick={() => handleEventClick(event)}>
                                  <img
                                    src={event.img}
                                    alt={event.title}
                                    style={{
                                      width: 54,
                                      height: 72,
                                      objectFit: 'cover',
                                      borderRadius: 4,
                                    }}
                                  />
                                </div>
                                <div
                                  slot="after"
                                  style={{ fontSize: '14px' }}
                                  role="button"
                                  onClick={() => handleEventClick(event)}
                                >
                                  More
                                </div>
                                <div slot="text">
                                  <RsvpCardListItemActions event={event} />
                                </div>
                              </F7ListItem>
                            ))}
                          </ListGroup>
                        ),
                      )}
                      {IS_MISSED_EVENTS_ENABLED && (
                        <F7ListItem>
                          <div>
                            <FormLabel text="+ Anything we missed?" />
                            <div className="mt-1">
                              <Textarea
                                key={currentUser?._id ?? 'unauthenticated'}
                                placeholder={`Did we miss anything?`}
                                onChange={setAdditionalPlans}
                              />
                            </div>
                          </div>
                        </F7ListItem>
                      )}
                    </F7List>
                  ) : (
                    currentFilter && (
                      <EmptyState title="No matching events" text="No events match this filter" />
                    )
                  )}
                </>
              ) : (
                <div className="d-grid gap-2">
                  <Skeleton height={120} />
                  <Skeleton height={120} />
                  <Skeleton height={120} />
                  <Skeleton height={120} />
                  <Skeleton height={120} />
                  <Skeleton height={120} />
                  <Skeleton height={120} />
                  <Skeleton height={120} />
                  <Skeleton height={120} />
                </div>
              )}
            </>
          ) : (
            <div className="d-grid gap-2">
              <Skeleton height={60} />
              <Skeleton height={40} />
              <Skeleton height={60} />
              <Skeleton height={80} />
              <Skeleton height={120} />
              <Skeleton height={120} />
              <Skeleton height={120} />
              <Skeleton height={120} />
              <Skeleton height={120} />
              <Skeleton height={120} />
              <Skeleton height={120} />
              <Skeleton height={120} />
            </div>
          )}
        </Screen.Content>

        <EventRsvpPopup
          isVisible={!!currentEvent}
          event={currentEvent}
          onClose={() => setCurrentEvent(undefined)}
        />
      </Screen>
    );
  },
);
